/**
 * v3.15-702: Unified Ghost Operational Assistant
 * On deal pages: Deal Assistant with context + action buttons + context-aware chat
 * On other pages: Briefing + tabs
 * NO separate popups, NO duplicate UIs.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import GhostDealBuilder from './GhostDealBuilder';
import GhostAICallDialog from './GhostAICallDialog';
// v3.18-1314 — Operator Console (terminal-style input + client header)
import { OperatorInput, LockedClientHeader } from './GhostOperatorConsole';
import { clientDealAmount as _clientDealAmount } from '../lib/funding_sources_read';
import { X, Clock, DollarSign, FileText, Package, Truck, Landmark, CheckCircle, AlertTriangle, ExternalLink, MessageSquare, Send, StickyNote, ListTodo, Phone, Loader2 } from 'lucide-react';

// v3.15-916: Ghost is now the SOLE source of task-due popups. Polls
// /api/ghost-ops/due-tasks every 60s and surfaces a Ghost-branded toast per
// task. On display we POST /api/tasks/{id}/mark-notified so the same task is
// never surfaced twice.
const GHOST_TASK_POLL_MS = 60_000;

const ICON_MAP = {
  clipboard: FileText, dollar: DollarSign, file: FileText,
  package: Package, truck: Truck, bank: Landmark,
  clock: Clock, check: CheckCircle,
};
const TYPE_COLORS = {
  success: 'text-green-600 bg-green-50',
  info: 'text-blue-600 bg-blue-50',
  warning: 'text-amber-600 bg-amber-50',
  alert: 'text-red-600 bg-red-50',
};

const STAGE_LABELS = {
  new_lead: 'New Lead',
  contacted: 'Contacted',
  application_sent: 'App Sent',
  application_received: 'App Received',
  submitted_to_lender: 'Submitted to Lender',
  approved: 'Approved',
  funded: 'Funded',
  settled: 'Settled',
  closed_declined: 'Declined',
  closed_cancelled: 'Cancelled',
};

const GhostFloatingIcon = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState('briefing');
  // v3.15-1141A: reactive awareness — close briefing immediately when the
  // non-dismissible Ghost Contact Awareness modal mounts so it cannot
  // intercept pointer events on the Acknowledge button.
  const [awarenessOpen, setAwarenessOpen] = useState(false);
  useEffect(() => {
    const sel = '[data-testid="ghost-contact-awareness-modal"]';
    const check = () => setAwarenessOpen(!!document.querySelector(sel));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);
  // When awareness appears, retract the briefing immediately.
  useEffect(() => {
    if (awarenessOpen && open) setOpen(false);
  }, [awarenessOpen, open]);
  const [briefingTab, setBriefingTab] = useState('summary');
  const [clientContext, setClientContext] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [hasShownOnLogin, setHasShownOnLogin] = useState(false);
  const [lastActivity, setLastActivity] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatSessionId] = useState(() => `ghost-${Date.now()}`);
  // v3.18-1313: Ghost Command Execution Engine — server-side session
  // that locks the operator to a selected client across turns.
  // v3.18-1314: Session ID is persisted to localStorage so a page reload
  // (or a fresh browser tab) still hydrates the locked client.
  const OPCMD_SESS_KEY = `ghost-opcmd-session-${user?.user_id || user?.id || 'anon'}`;
  const [cmdSessionId, setCmdSessionId] = useState(() => {
    try { return localStorage.getItem(OPCMD_SESS_KEY); } catch { return null; }
  });
  const [cmdSelectedClient, setCmdSelectedClient] = useState(null);
  // v3.18-1322 — Ghost is team-aware.  Auto-loaded operator team
  // memberships (populated on mount, refreshed alongside session
  // context hydration).
  const [operatorTeams, setOperatorTeams] = useState([]);
  const chatEndRef = useRef(null);

  // Persist session id whenever it changes.
  useEffect(() => {
    if (!cmdSessionId) return;
    try { localStorage.setItem(OPCMD_SESS_KEY, cmdSessionId); } catch { /* ignore */ }
  }, [cmdSessionId, OPCMD_SESS_KEY]);

  // v3.18-1322: Fetch the operator's team memberships once per mount.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/ghost/command-engine/operator-context');
        if (!cancelled) setOperatorTeams(r.data?.teams || []);
      } catch { /* silent — non-operator or auth failure */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // v3.18-1314: Hydrate the locked client on mount / whenever we have a
  // session id but no client object yet.  Silent — never toasts on failure.
  useEffect(() => {
    if (!cmdSessionId || cmdSelectedClient) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/ghost/command-engine/session/${cmdSessionId}/context`);
        if (!cancelled && r.data?.selected_client) {
          setCmdSelectedClient(r.data.selected_client);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [cmdSessionId, cmdSelectedClient]);

  // Detect client context from URL — enhanced with missing items + last activity
  useEffect(() => {
    const match = location.pathname.match(/\/clients\/([^/]+)/);
    if (match) {
      const clientId = match[1];
      Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/clients/${clientId}/activities`).catch(() => ({ data: [] })),
      ]).then(([cRes, aRes]) => {
        const c = cRes.data;
        const missing = [];
        if (!c.ssn) missing.push('SSN');
        if (!c.dob && !c.date_of_birth) missing.push('DOB');
        if (!c.income && !c.monthly_income) missing.push('Income');
        if (!c.employment && !c.employer_name) missing.push('Employment');
        if (!c.residence_type) missing.push('Residence');
        if (!c.loan_amount && !c.loan_amount_requested) missing.push('Loan Amount');
        if (!c.street_address && !c.address) missing.push('Address');

        // v3.18-1290 PAYMENT-008B Slice 5 — deal amount pulled through
        // the shared funding_sources_read helper.  Legacy c.deal_amount
        // remains a compatibility fallback only.
        setClientContext({
          id: c.id, full_name: c.full_name, email: c.email, phone: c.phone,
          deal_amount: _clientDealAmount(c),
          funding_sources: Array.isArray(c.funding_sources) ? c.funding_sources : [],
          deal_stage: c.deal_stage,
          company_name: c.company_name, missing_items: missing,
        });

        const activities = Array.isArray(aRes.data) ? aRes.data : (aRes.data?.activities || []);
        if (activities.length > 0) {
          const latest = activities[0];
          setLastActivity(latest.content || latest.description || latest.type || 'Activity recorded');
        } else {
          setLastActivity(null);
        }
      }).catch(() => {
        setClientContext(null);
        setLastActivity(null);
      });
    } else {
      setClientContext(null);
      setLastActivity(null);
    }
  }, [location.pathname]);

  const fetchBriefing = useCallback(async () => {
    console.log('[Ghost] Fetching live briefing data...');
    try {
      const [bRes, aRes] = await Promise.all([
        api.get('/ghost-ops/briefing'),
        api.get('/ghost-ops/alerts?limit=15'),
      ]);
      setBriefing(bRes.data);
      setAlerts(aRes.data?.alerts || []);
    } catch (err) {
      console.error('[Ghost] Briefing fetch error:', err);
    }
  }, []);

  // v3.15-916: Ghost-driven task-due popups. Replaces the standalone
  // TaskDueNotifier — Ghost is the only source of these alerts. Polls every
  // 60s, dedupes per-session via shownTaskIdsRef, and marks-notified on
  // display so a task is never surfaced twice.
  const shownTaskIdsRef = useRef(new Set());
  useEffect(() => {
    if (!user?.id && !user?.user_id) return;
    let alive = true;

    const tick = async () => {
      try {
        const res = await api.get('/ghost-ops/due-tasks');
        const items = res.data?.items || [];
        for (const t of items) {
          if (!t?.task_id) continue;
          if (shownTaskIdsRef.current.has(t.task_id)) continue;
          shownTaskIdsRef.current.add(t.task_id);

          // Ghost-branded popup. "Open Task" jumps to the client; "Dismiss"
          // closes the toast. Both paths mark the task as notified so the
          // same task never re-fires on the next 60s tick.
          const markNotified = () => {
            void api.post(`/tasks/${t.task_id}/mark-notified`).catch(() => {});
          };

          (t.escalated ? toast.error : toast.warning)(
            <div data-testid={`ghost-task-popup-${t.task_id}`} className="flex items-start gap-2">
              <img src="/ghost-icon.png" alt="Ghost" className="w-5 h-5 object-contain flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Ghost AI</div>
                <div className="font-medium text-sm">{t.ghost_message}</div>
                {t.escalated && t.overdue_minutes > 0 && (
                  <div className="text-[11px] text-red-600 mt-0.5">Overdue by {t.overdue_minutes} min</div>
                )}
              </div>
            </div>,
            {
              duration: t.escalated ? 18000 : 12000,
              action: {
                label: t.client_id ? 'Open Task' : 'Got it',
                onClick: () => {
                  if (t.client_id) navigate(`/clients/${t.client_id}`);
                },
              },
              cancel: {
                label: 'Dismiss',
                onClick: () => {},
              },
              onAutoClose: markNotified,
              onDismiss:   markNotified,
            }
          );
        }
      } catch {
        // silent — try again on next poll (auth blip / scope change / network).
      }
    };

    tick();
    const id = setInterval(() => { if (alive) tick(); }, GHOST_TASK_POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [user?.id, user?.user_id, navigate]);

  // v3.15-926: Appointment pending-outcome popup. Polls every 2 min for
  // appointments whose end_time has passed but still need an outcome
  // (Completed / No Show / Rescheduled). Ghost fires a sticky red toast per
  // appointment; the toast includes "Log Outcome" that deep-links to the
  // client detail page (where the appointment details modal can be opened).
  const shownApptIdsRef = useRef(new Set());
  useEffect(() => {
    if (!user?.id && !user?.user_id) return;
    let alive = true;

    const tickAppt = async () => {
      try {
        const res = await api.get('/appointments/pending-outcome');
        const items = res.data?.items || [];
        for (const a of items) {
          if (!a?.id) continue;
          if (shownApptIdsRef.current.has(a.id)) continue;
          shownApptIdsRef.current.add(a.id);
          toast.error(
            <div data-testid={`ghost-appt-pending-${a.id}`} className="flex items-start gap-2">
              <img src="/ghost-icon.png" alt="Ghost" className="w-5 h-5 object-contain flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Ghost AI</div>
                <div className="font-medium text-sm">Appointment requires outcome</div>
                <div className="text-[11px] text-slate-600 mt-0.5">{a.client_name || 'Client'} • ended {new Date(a.date_time_end).toLocaleString('en-US', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}</div>
              </div>
            </div>,
            {
              duration: Infinity,   // sticky — spec says block dismissal
              action: {
                label: 'Log Outcome',
                onClick: () => {
                  if (a.client_id) navigate(`/clients/${a.client_id}?appt=${a.id}`);
                },
              },
            }
          );
        }
      } catch { /* silent */ }
    };

    tickAppt();
    const id = setInterval(() => { if (alive) tickAppt(); }, 120_000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.id, user?.user_id, navigate]);

  // v3.15-1148 — Auto-show on login is REMOVED per Ghost Cleanup directive.
  // Briefing / Builder / Closer are now MANUAL-INVOCATION ONLY (click the
  // floating icon). The non-dismissible Ghost Contact Awareness modal and
  // the pending-outcome reminder remain auto-fired (they drive action).
  useEffect(() => {
    if (user && !hasShownOnLogin) {
      // Still pre-fetch the briefing so the first manual click is fast,
      // but DO NOT auto-open the panel.
      fetchBriefing().then(() => setHasShownOnLogin(true));
    }
  }, [user, hasShownOnLogin, fetchBriefing]);

  const handleOpen = () => {
    console.log('[Ghost] Ghost button clicked');
    if (clientContext) {
      // On a deal page — open deal assistant directly
      setView('deal_assistant');
    } else {
      // On other pages — open briefing
      fetchBriefing();
      setBriefingTab('summary');
      setView('briefing');
    }
    setOpen(true);
  };

  const handleOpenBuilder = () => {
    console.log('[Ghost] Navigating to Ghost Deal Builder');
    setView('builder');
  };

  const handleTabSwitch = (tabId) => {
    setBriefingTab(tabId);
  };

  const handleAction = (action, clientId) => {
    console.log('[Ghost] Action triggered:', action, 'clientId:', clientId);
    if (action === 'open_deal' && clientId) {
      navigate(`/clients/${clientId}`);
      setOpen(false);
    }
  };

  const handleBuilderClose = () => {
    setView('briefing');
    setOpen(false);
  };

  const handleDealCreated = (deal) => {
    console.log('[Ghost] Deal created:', deal?.full_name || deal?.client_id);
    fetchBriefing();
  };

  // Deal Assistant action handlers — trigger existing page elements
  const handleSendForOffers = () => {
    console.log('[Ghost] Action: Send for Offers');
    setOpen(false);
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="send-for-offers-client-1"]');
      if (btn) { btn.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => btn.click(), 300); return; }
      // Fallback: find any Send for Offers button
      const allBtns = document.querySelectorAll('button');
      for (const b of allBtns) {
        if (b.textContent?.includes('Send for Offers')) { b.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => b.click(), 300); return; }
      }
    }, 200);
  };

  const handleAddNote = () => {
    console.log('[Ghost] Action: Add Note');
    setOpen(false);
    setTimeout(() => {
      const noteInput = document.querySelector('[data-testid="activity-add-note-input"]') || document.querySelector('[data-testid="add-note-textarea"]');
      if (noteInput) {
        noteInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => noteInput.focus(), 300);
      }
    }, 200);
  };

  const handleCreateTask = () => {
    console.log('[Ghost] Action: Create Task');
    setOpen(false);
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="create-task-action-btn"]');
      if (btn) { btn.scrollIntoView({ behavior: 'smooth', block: 'center' }); btn.click(); }
    }, 200);
  };

  const handleCallClient = () => {
    console.log('[Ghost] Action: Call Client — surface local-time awareness first');
    setOpen(false);
    setCallDialogOpen(true);
  };

  // v3.18-1289 — Ghost AI Call Dialog surfaces the reusable
  // <ClientLocalTime> component with quiet-hours coloring before we
  // actually initiate the click-to-call.  Operator remains in control:
  // Call Now is never disabled.
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const executeCall = () => {
    setTimeout(() => {
      const phoneBtn = document.querySelector('[data-testid="phone-click-to-call"]');
      if (phoneBtn) { phoneBtn.click(); return; }
      const phoneLinks = document.querySelectorAll('a[href^="tel:"], [data-testid*="phone"], [data-testid*="dial"]');
      if (phoneLinks.length > 0) { phoneLinks[0].click(); return; }
      const dialBtn = document.querySelector('[data-testid="header-dial-btn"], button:has-text("Dial")');
      if (dialBtn) dialBtn.click();
    }, 200);
  };

  // v3.15-702: Context-aware Ghost chat
  // v3.18-1313: Operator Command Execution Engine intercepts commands
  // that match the internal grammar (verbs / IDs / yes/no).
  // v3.21-1630: Non-command free-form chat now flows to the unified
  // Ghost brain at /api/ghost/answer (capability='global').
  const INTERNAL_ORGS = new Set([
    'prestigious travel club', 'easyquickfund', 'easy quick fund',
    'prestigious companies', 'ptc',
  ]);
  const isInternalOperator = !!(
    user && INTERNAL_ORGS.has(String(user.company_name || '').trim().toLowerCase())
  );
  const looksLikeOperatorCommand = (text) => {
    if (!isInternalOperator) return false;
    const t = String(text || '').trim();
    if (!t) return false;
    if (/\b(EQF|APP|APPLICATION|CONF|CONFIRMATION|DEAL)[-\s]?[A-Z0-9]{4,}\b/i.test(t)) return true;
    if (/^(mark|set|make|change|move|put|reactivate|activate|deactivate|archive|reinstate|update|correct|add|append|log|save|write|clear|reset|new)\b/i.test(t)) return true;
    if (/^note\s*[:\-]/i.test(t)) return true;
    // v3.18-1322 — Team-aware "my teams" / "teams" query routes through
    // the operator engine (returns a plan with the operator's teams).
    if (/^(my\s+teams|teams|list\s+teams|what\s+teams\s+am\s+i\s+on)\s*[\?\.]?$/i.test(t)) return true;
    if (cmdSessionId && /^(yes|y|no|n|confirm|ok|okay|cancel|abort|stop|proceed|apply)\b/i.test(t)) return true;
    return false;
  };

  const handleChatSend = async (overrideMsg) => {
    const msg = (overrideMsg !== undefined ? overrideMsg : chatInput).trim();
    if (!msg || chatSending) return;

    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatInput('');
    setChatSending(true);

    // v3.18-1313: Command execution branch — Operator Mode
    if (looksLikeOperatorCommand(msg)) {
      try {
        // If the operator is on a client page and no session lock exists,
        // seed the session with that client so context memory kicks in.
        const seed = (!cmdSelectedClient && clientContext?.id)
          ? ` (client id: ${clientContext.id})` : '';
        const res = await api.post('/ghost/command-engine/execute', {
          text: msg + seed,
          session_id: cmdSessionId,
        });
        const d = res.data || {};
        if (d.session_id) setCmdSessionId(d.session_id);
        if (d.selected_client) setCmdSelectedClient(d.selected_client);
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: d.message || (d.ok ? 'Done.' : 'Command failed.'),
          isOperatorResponse: true,
          operatorOk:        !!d.ok,
          operatorPlan:      d.plan || null,
          operatorClient:    d.selected_client || null,
          operatorCandidates:d.candidates || null,
          operatorFieldDiffs:d.field_diffs || null,
        }]);
      } catch (err) {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: String(err?.response?.data?.detail || 'Command failed.'),
          isOperatorResponse: true, operatorOk: false,
        }]);
      } finally {
        setChatSending(false);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
      return;
    }

    // v3.21-1630 · Non-command chat routes through the UNIFIED Ghost
    // brain so the floating icon delivers the same intent-aware,
    // approved-script-first, source-labeled responses as Client
    // Workspace Ghost, Call Assist and Onboarding Modal.  Previously
    // this bypassed the unified brain by hitting the deal-focused
    // chat endpoint (with its own hallucination-prone system prompt),
    // which is why "What is the origination fee?" returned made-up
    // "$3,000-$7,000 per deed" pricing instead of the approved
    // lender-fee guidance.
    try {
      const res = await api.post('/ghost/answer', {
        query:            msg,
        capability:       'global',
        active_client_id: clientContext?.id || undefined,
      });
      const d = res.data || {};
      const body   = d.body || d.formatted_response || d.message || 'No response';
      const source = d.source || 'suggested_wording';
      const badge  = source === 'approved_script'       ? 'Approved Script'
                   : source === 'operational_knowledge' ? 'Approved Guidance'
                   : source === 'data'                  ? 'Hub Data'
                   : source === 'query_help'            ? 'Ghost Help'
                   :                                       'Suggested Wording — review before using';
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `[${badge}]\n${body}`,
        ghostSource: source,
      }]);
    } catch (err) {
      console.error('[Ghost] Chat error:', err);
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Ghost unavailable — try again' }]);
    } finally {
      setChatSending(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  // v3.15-1004: Ghost is now visible for ALL authenticated users — internal,
  // external partners, and VO officers. Backend scope_guard already enforces
  // that Ghost endpoints (/api/ghost/*, /api/ghost-ops/*, /api/ghost-deal/*)
  // only return data the caller is allowed to see, so external/VO users get
  // a strictly-scoped Ghost worldview (their assigned clients/deals only).
  // Previously Ghost was internal-only — that hid it from the very users who
  // need it most as their primary workspace assistant after v3.15-1002 took
  // away Dashboard/Calendar/Availability.
  if (!user) return null;

  const actionAlerts = alerts.filter(a => a.category === 'action_required');
  const moneyAlerts = alerts.filter(a => a.category === 'money_movement');
  const generalAlerts = alerts.filter(a => a.category === 'general');

  return createPortal(
    <>
      {/* v3.18-1289 — Ghost AI Call Dialog: displays the reusable
          ClientLocalTime banner + quiet-hours theming before the
          click-to-call fires.  Operator remains in control. */}
      <GhostAICallDialog
        open={callDialogOpen}
        onOpenChange={setCallDialogOpen}
        clientId={clientContext?.id}
        clientName={clientContext?.full_name}
        onCallNow={executeCall}
      />

      {/* Single floating button — BRANDED GHOST ICON (DO NOT CHANGE) */}
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-slate-900 flex items-center justify-center shadow-lg hover:bg-slate-800 transition-colors overflow-hidden"
        style={{ zIndex: 99998, pointerEvents: 'auto' }}
        data-testid="ghost-floating-icon"
        title="Ghost Assistant"
      >
        <img src="/ghost-icon.png" alt="Ghost" className="w-10 h-10 object-contain" />
      </button>

      {/* DEAL ASSISTANT — shown when on a deal page */}
      {/* v3.15-1141A: also suppressed when the awareness modal is up. */}
      {open && view === 'deal_assistant' && clientContext && !awarenessOpen && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999, pointerEvents: 'auto' }}>
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col" style={{ pointerEvents: 'auto' }} data-testid="ghost-deal-assistant">
            {/* Header */}
            <div className="px-5 pt-5 pb-3 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src="/ghost-icon.png" alt="Ghost" className="w-10 h-10 object-contain" />
                  <div>
                    <p className="font-heading font-bold text-base text-slate-900">Ghost AI</p>
                    <p className="text-[10px] text-slate-400 leading-tight">Deal Assistant</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded" data-testid="ghost-close-btn"><X className="h-4 w-4 text-slate-400" /></button>
              </div>
            </div>

            {/* Deal Context */}
            <div className="px-5 py-4 space-y-3" data-testid="ghost-deal-context">
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 uppercase font-semibold">Client</span>
                  <span className="text-xs font-bold text-slate-800">{clientContext.full_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 uppercase font-semibold">Status</span>
                  <span className="text-xs font-medium text-blue-600">{STAGE_LABELS[clientContext.deal_stage] || clientContext.deal_stage || 'No Stage'}</span>
                </div>
                {clientContext.deal_amount && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Deal Amount</span>
                    <span className="text-xs font-medium text-emerald-600">${Number(clientContext.deal_amount).toLocaleString()}</span>
                  </div>
                )}
                {lastActivity && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Last Activity</span>
                    <span className="text-xs text-slate-600 truncate max-w-[200px]">{lastActivity}</span>
                  </div>
                )}
              </div>

              {/* Missing Items */}
              {clientContext.missing_items?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                    <span className="text-[10px] text-amber-700 uppercase font-semibold">Missing Items</span>
                  </div>
                  <p className="text-xs text-amber-800">{clientContext.missing_items.join(', ')}</p>
                </div>
              )}
              {clientContext.missing_items?.length === 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs text-green-700 font-medium">All required fields complete</span>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="px-5 pb-3 space-y-1.5" data-testid="ghost-deal-actions">
              <button onClick={handleSendForOffers} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors text-left" data-testid="ghost-action-send-offers">
                <Send className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="text-xs font-medium">Send for Offers</span>
              </button>
              <div className="grid grid-cols-3 gap-1.5">
                <button onClick={handleAddNote} className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors" data-testid="ghost-action-add-note">
                  <StickyNote className="h-3 w-3" /><span className="text-[10px] font-medium">Add Note</span>
                </button>
                <button onClick={handleCreateTask} className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors" data-testid="ghost-action-create-task">
                  <ListTodo className="h-3 w-3" /><span className="text-[10px] font-medium">Create Task</span>
                </button>
                <button onClick={handleCallClient} className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors" data-testid="ghost-action-call-client">
                  <Phone className="h-3 w-3" /><span className="text-[10px] font-medium">Call</span>
                </button>
              </div>
            </div>

            {/* v3.15-702: Chat Thread */}
            {chatMessages.length > 0 && (
              <div className="px-5 pb-2 max-h-[180px] overflow-y-auto" data-testid="ghost-chat-thread">
                <div className="space-y-2">
                  {chatMessages.map((msg, i) => (
                    <div key={`chat-${i}-${msg.role}`}>
                      <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] px-3 py-1.5 rounded-lg text-xs leading-relaxed ${
                          msg.role === 'user'
                            ? 'bg-slate-800 text-white'
                            : msg.isOperatorResponse
                              ? (msg.operatorOk
                                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-900 font-mono whitespace-pre-wrap'
                                  : 'bg-red-50 border border-red-200 text-red-900 font-mono whitespace-pre-wrap')
                              : 'bg-slate-100 text-slate-700'
                        }`}
                        data-testid={msg.role === 'user' ? undefined : (msg.isOperatorResponse ? 'ghost-operator-response' : undefined)}>
                          {msg.content}
                        </div>
                      </div>
                      {msg.role === 'assistant' && msg.operatorPlan && (
                        <div className="mt-1 border border-emerald-200 bg-white rounded p-2 text-[10px] font-mono space-y-0.5"
                             data-testid="ghost-operator-plan">
                          <div><span className="text-slate-400 uppercase mr-1">Client</span><b>{msg.operatorPlan.client}</b></div>
                          <div><span className="text-slate-400 uppercase mr-1">Action</span>{msg.operatorPlan.action}</div>
                          <div><span className="text-slate-400 uppercase mr-1">Current</span><span className="text-slate-600">{msg.operatorPlan.current}</span></div>
                          <div><span className="text-slate-400 uppercase mr-1">New</span><span className="text-emerald-700 font-semibold">{msg.operatorPlan.new}</span></div>
                          <div><span className="text-slate-400 uppercase mr-1">Record</span><span className="text-slate-500">{msg.operatorPlan.record}</span></div>
                        </div>
                      )}
                      {msg.role === 'assistant' && Array.isArray(msg.operatorCandidates) && msg.operatorCandidates.length > 0 && (
                        <div className="mt-1 space-y-1" data-testid="ghost-operator-candidates">
                          {msg.operatorCandidates.map((c) => (
                            <button key={c.id}
                              onClick={async () => {
                                setChatSending(true);
                                try {
                                  const r = await api.post('/ghost/command-engine/execute', {
                                    text: '', session_id: cmdSessionId, pick_client_id: c.id,
                                  });
                                  const d = r.data || {};
                                  if (d.session_id) setCmdSessionId(d.session_id);
                                  if (d.selected_client) setCmdSelectedClient(d.selected_client);
                                  setChatMessages(prev => [...prev, {
                                    role: 'assistant', content: d.message || '',
                                    isOperatorResponse: true, operatorOk: !!d.ok,
                                    operatorClient: d.selected_client || null,
                                  }]);
                                } finally { setChatSending(false); }
                              }}
                              className="w-full text-left text-[10px] font-mono bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 px-2 py-1.5 rounded transition-colors"
                              data-testid={`ghost-operator-candidate-${c.id}`}>
                              <b>{c.full_name}</b>
                              <span className="text-slate-400"> · {c.record}</span>
                              {c.email && <span className="text-slate-500"> · {c.email}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {msg.role === 'assistant' && Array.isArray(msg.operatorFieldDiffs) && msg.operatorFieldDiffs.length > 0 && (
                        <div className="mt-1 border border-amber-200 bg-amber-50 rounded p-2 text-[10px] font-mono space-y-1"
                             data-testid="ghost-operator-field-diff">
                          <div className="uppercase text-amber-700 font-semibold">Supplied differs from record</div>
                          <table className="w-full">
                            <thead>
                              <tr className="text-slate-500"><th className="text-left">Field</th><th className="text-left">Current</th><th className="text-left">Supplied</th></tr>
                            </thead>
                            <tbody>
                              {msg.operatorFieldDiffs.map((d, k) => (
                                <tr key={k} className="border-t border-amber-200">
                                  <td className="py-0.5 pr-2 font-semibold">{d.field}</td>
                                  <td className="py-0.5 pr-2 text-slate-600">{d.current || '—'}</td>
                                  <td className="py-0.5 text-emerald-700 font-semibold">{d.supplied}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="flex gap-1 pt-0.5">
                            <button onClick={() => { setChatInput('yes'); setTimeout(handleChatSend, 50); }}
                                    className="px-2 py-0.5 bg-emerald-600 text-white rounded text-[9px] font-semibold hover:bg-emerald-700"
                                    data-testid="ghost-operator-diff-apply">
                              Apply
                            </button>
                            <button onClick={() => { setChatInput('no'); setTimeout(handleChatSend, 50); }}
                                    className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded text-[9px] hover:bg-slate-50"
                                    data-testid="ghost-operator-diff-cancel">
                              Keep current
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {chatSending && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 rounded-lg px-3 py-1.5"><Loader2 className="h-3 w-3 animate-spin text-slate-400" /></div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </div>
            )}

            {/* Chat Input */}
            <div className="px-5 py-2.5 border-t">
              {/* v3.18-1314: Locked client header for internal operators */}
              {isInternalOperator && cmdSelectedClient && (
                <LockedClientHeader
                  client={cmdSelectedClient}
                  operatorTeams={operatorTeams}
                  onClear={() => {
                    setCmdSelectedClient(null);
                    handleChatSend('clear context');
                  }} />
              )}
              {isInternalOperator && !cmdSelectedClient && operatorTeams.length > 0 && (
                <div className="bg-slate-950 text-slate-300 border-b border-slate-800 font-mono px-3 py-1.5 text-[10px] flex items-center gap-1 flex-wrap"
                     data-testid="ghost-operator-teams-idle">
                  <span className="text-slate-500 uppercase text-[9px] mr-0.5">You:</span>
                  {operatorTeams.map(t => (
                    <span key={t.id}
                          className={`text-[9px] px-1.5 py-0.5 rounded border ${
                            t.team_type === 'vo' ? 'bg-indigo-950 border-indigo-700 text-indigo-200'
                          : t.team_type === 'sales' ? 'bg-blue-950 border-blue-700 text-blue-200'
                          : t.team_type === 'concierge' ? 'bg-teal-950 border-teal-700 text-teal-200'
                          : t.team_type === 'funding' ? 'bg-amber-950 border-amber-700 text-amber-200'
                          : t.team_type === 'applications' ? 'bg-violet-950 border-violet-700 text-violet-200'
                          :                         'bg-slate-800 border-slate-600 text-slate-200'
                          }`}
                          title={`${t.team_type_label}${t.is_lead ? ' — Team Lead' : ''}`}>
                      {t.is_lead ? '★ ' : ''}{t.name}
                    </span>
                  ))}
                </div>
              )}
              <div className={isInternalOperator ? '' : 'flex items-center gap-2'}>
                {isInternalOperator ? (
                  <OperatorInput
                    userId={user?.user_id || user?.id}
                    value={chatInput}
                    onChange={setChatInput}
                    onSubmit={(v) => handleChatSend(v)}
                    disabled={chatSending}
                    locked={!!cmdSelectedClient}
                    placeholder="Command Ghost (e.g. Mark John Doe Active)"
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={handleChatKeyDown}
                      placeholder="Ask Ghost about this deal..."
                      className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      disabled={chatSending}
                      data-testid="ghost-chat-input"
                    />
                    <button
                      onClick={() => handleChatSend()}
                      disabled={chatSending || !chatInput.trim()}
                      className="p-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40 transition-colors"
                      data-testid="ghost-chat-send-btn"
                    >
                      <Send className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
              <button onClick={() => { fetchBriefing(); setView('briefing'); }} className="w-full text-center text-[10px] text-slate-400 hover:text-slate-600 py-1 mt-1 transition-colors" data-testid="ghost-switch-briefing">
                View Daily Briefing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BRIEFING VIEW — shown when NOT on a deal page */}
      {/* v3.15-1141A: reactive suppression — briefing must NOT render while a
          Ghost Contact Awareness modal is on screen. `awarenessOpen` is fed
          by a MutationObserver above so the suppression re-evaluates on mount. */}
      {open && view === 'briefing' && !awarenessOpen && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999, pointerEvents: awarenessOpen ? 'none' : 'auto' }}>
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col" style={{ pointerEvents: 'auto' }} data-testid="ghost-briefing-panel">
            {/* Header — BRANDED: DO NOT ALTER */}
            <div className="px-5 pt-5 pb-3 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src="/ghost-icon.png" alt="Ghost" className="w-10 h-10 object-contain" />
                  <div>
                    <p className="font-heading font-bold text-base text-slate-900">Ghost AI</p>
                    <p className="text-[10px] text-slate-400 leading-tight">powered by Prestigious Companies</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded" data-testid="ghost-close-btn"><X className="h-4 w-4 text-slate-400" /></button>
              </div>
              {briefing && (
                <p className="text-sm text-slate-600 mt-2">{briefing.greeting} — <span className="text-slate-400">{briefing.date}</span></p>
              )}
              <div className="flex gap-1 mt-3">
                {[
                  { id: 'summary', label: "Today's Summary" },
                  { id: 'attention', label: 'Needs Attention', count: actionAlerts.length },
                  { id: 'completed', label: 'Completed', count: moneyAlerts.length },
                ].map(t => (
                  <button key={t.id} onClick={() => handleTabSwitch(t.id)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${briefingTab === t.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid={`ghost-tab-${t.id}`}>
                    {t.label}{t.count > 0 ? ` (${t.count})` : ''}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3" data-testid="ghost-content">
              {briefingTab === 'summary' && briefing && (
                <div className="space-y-2">
                  {briefing.summary.map((item, i) => {
                    const Icon = ICON_MAP[item.icon] || CheckCircle;
                    const colors = TYPE_COLORS[item.type] || 'text-slate-600 bg-slate-50';
                    return (
                      <div key={`summary-${item.text?.slice(0,20) || i}`} className={`flex items-center gap-3 p-2.5 rounded-lg ${colors.split(' ')[1]}`}>
                        <Icon className={`h-4 w-4 flex-shrink-0 ${colors.split(' ')[0]}`} />
                        <span className="text-sm text-slate-700">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {briefingTab === 'attention' && (
                <div className="space-y-2">
                  {actionAlerts.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No items need attention</p>}
                  {actionAlerts.map((alert, i) => (
                    <div key={i} className="p-3 rounded-lg border border-red-100 bg-red-50/50" data-testid={`ghost-alert-${i}`}>
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-slate-700">{alert.message}</p>
                          <div className="flex gap-1.5 mt-2">
                            {alert.actions?.includes('open_deal') && (
                              <button onClick={() => handleAction('open_deal', alert.client_id)} className="text-[10px] px-2 py-0.5 bg-white border rounded-full text-slate-600 hover:bg-slate-50 flex items-center gap-1" data-testid={`ghost-open-deal-${i}`}>
                                <ExternalLink className="h-2.5 w-2.5" />Open Deal
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {briefingTab === 'completed' && (
                <div className="space-y-2">
                  {moneyAlerts.length === 0 && generalAlerts.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No completed items today</p>}
                  {[...moneyAlerts, ...generalAlerts].map((alert, i) => (
                    <div key={alert.client_id || `comp-${i}`} className={`p-3 rounded-lg border ${alert.type === 'funded' || alert.type === 'delivered' ? 'border-green-100 bg-green-50/50' : 'border-blue-100 bg-blue-50/50'}`}>
                      <div className="flex items-start gap-2">
                        {alert.type === 'funded' || alert.type === 'delivered' ? <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" /> : <DollarSign className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />}
                        <div className="flex-1">
                          <p className="text-xs text-slate-700">{alert.message}</p>
                          {alert.actions?.includes('open_deal') && (
                            <button onClick={() => handleAction('open_deal', alert.client_id)} className="mt-1.5 text-[10px] px-2 py-0.5 bg-white border rounded-full text-slate-600 hover:bg-slate-50 flex items-center gap-1">
                              <ExternalLink className="h-2.5 w-2.5" />Open Deal
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* v3.15-702: Chat Thread in Briefing View */}
            {chatMessages.length > 0 && (
              <div className="px-5 pb-2 max-h-[220px] overflow-y-auto border-t pt-2" data-testid="ghost-briefing-chat-thread">
                <div className="space-y-2">
                  {chatMessages.map((msg, i) => (
                    <div key={`bchat-${i}-${msg.role}`}>
                      <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] px-3 py-1.5 rounded-lg text-xs leading-relaxed ${
                          msg.role === 'user'
                            ? 'bg-slate-800 text-white'
                            : msg.isOperatorResponse
                              ? (msg.operatorOk
                                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-900 font-mono whitespace-pre-wrap'
                                  : 'bg-red-50 border border-red-200 text-red-900 font-mono whitespace-pre-wrap')
                              : 'bg-slate-100 text-slate-700'
                        }`}
                        data-testid={msg.role === 'user' ? undefined : (msg.isOperatorResponse ? 'ghost-operator-response' : undefined)}>
                          {msg.content}
                        </div>
                      </div>
                      {msg.role === 'assistant' && msg.operatorPlan && (
                        <div className="mt-1 border border-emerald-200 bg-white rounded p-2 text-[10px] font-mono space-y-0.5"
                             data-testid="ghost-operator-plan">
                          <div><span className="text-slate-400 uppercase mr-1">Client</span><b>{msg.operatorPlan.client}</b></div>
                          <div><span className="text-slate-400 uppercase mr-1">Action</span>{msg.operatorPlan.action}</div>
                          <div><span className="text-slate-400 uppercase mr-1">Current</span><span className="text-slate-600">{msg.operatorPlan.current}</span></div>
                          <div><span className="text-slate-400 uppercase mr-1">New</span><span className="text-emerald-700 font-semibold">{msg.operatorPlan.new}</span></div>
                          <div><span className="text-slate-400 uppercase mr-1">Record</span><span className="text-slate-500">{msg.operatorPlan.record}</span></div>
                        </div>
                      )}
                      {msg.role === 'assistant' && Array.isArray(msg.operatorCandidates) && msg.operatorCandidates.length > 0 && (
                        <div className="mt-1 space-y-1" data-testid="ghost-operator-candidates">
                          {msg.operatorCandidates.map((c) => (
                            <button key={c.id}
                              onClick={async () => {
                                setChatSending(true);
                                try {
                                  const r = await api.post('/ghost/command-engine/execute', {
                                    text: '', session_id: cmdSessionId, pick_client_id: c.id,
                                  });
                                  const d = r.data || {};
                                  if (d.session_id) setCmdSessionId(d.session_id);
                                  if (d.selected_client) setCmdSelectedClient(d.selected_client);
                                  setChatMessages(prev => [...prev, {
                                    role: 'assistant', content: d.message || '',
                                    isOperatorResponse: true, operatorOk: !!d.ok,
                                    operatorClient: d.selected_client || null,
                                  }]);
                                } finally { setChatSending(false); }
                              }}
                              className="w-full text-left text-[10px] font-mono bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 px-2 py-1.5 rounded transition-colors"
                              data-testid={`ghost-operator-candidate-${c.id}`}>
                              <b>{c.full_name}</b>
                              <span className="text-slate-400"> · {c.record}</span>
                              {c.email && <span className="text-slate-500"> · {c.email}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {msg.role === 'assistant' && Array.isArray(msg.operatorFieldDiffs) && msg.operatorFieldDiffs.length > 0 && (
                        <div className="mt-1 border border-amber-200 bg-amber-50 rounded p-2 text-[10px] font-mono space-y-1"
                             data-testid="ghost-operator-field-diff">
                          <div className="uppercase text-amber-700 font-semibold">Supplied differs from record</div>
                          <table className="w-full">
                            <thead>
                              <tr className="text-slate-500"><th className="text-left">Field</th><th className="text-left">Current</th><th className="text-left">Supplied</th></tr>
                            </thead>
                            <tbody>
                              {msg.operatorFieldDiffs.map((d, k) => (
                                <tr key={k} className="border-t border-amber-200">
                                  <td className="py-0.5 pr-2 font-semibold">{d.field}</td>
                                  <td className="py-0.5 pr-2 text-slate-600">{d.current || '—'}</td>
                                  <td className="py-0.5 text-emerald-700 font-semibold">{d.supplied}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="flex gap-1 pt-0.5">
                            <button onClick={() => { setChatInput('yes'); setTimeout(handleChatSend, 50); }}
                                    className="px-2 py-0.5 bg-emerald-600 text-white rounded text-[9px] font-semibold hover:bg-emerald-700"
                                    data-testid="ghost-operator-diff-apply">
                              Apply
                            </button>
                            <button onClick={() => { setChatInput('no'); setTimeout(handleChatSend, 50); }}
                                    className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded text-[9px] hover:bg-slate-50"
                                    data-testid="ghost-operator-diff-cancel">
                              Keep current
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {chatSending && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 rounded-lg px-3 py-1.5"><Loader2 className="h-3 w-3 animate-spin text-slate-400" /></div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </div>
            )}

            {/* Chat Input + Builder Link */}
            <div className="px-5 py-3 border-t space-y-2">
              {/* v3.18-1314: Locked client header for internal operators */}
              {isInternalOperator && cmdSelectedClient && (
                <LockedClientHeader
                  client={cmdSelectedClient}
                  operatorTeams={operatorTeams}
                  onClear={() => {
                    setCmdSelectedClient(null);
                    handleChatSend('clear context');
                  }} />
              )}
              {isInternalOperator && !cmdSelectedClient && operatorTeams.length > 0 && (
                <div className="bg-slate-950 text-slate-300 border-b border-slate-800 font-mono px-3 py-1.5 text-[10px] flex items-center gap-1 flex-wrap"
                     data-testid="ghost-operator-teams-idle">
                  <span className="text-slate-500 uppercase text-[9px] mr-0.5">You:</span>
                  {operatorTeams.map(t => (
                    <span key={t.id}
                          className={`text-[9px] px-1.5 py-0.5 rounded border ${
                            t.team_type === 'vo' ? 'bg-indigo-950 border-indigo-700 text-indigo-200'
                          : t.team_type === 'sales' ? 'bg-blue-950 border-blue-700 text-blue-200'
                          : t.team_type === 'concierge' ? 'bg-teal-950 border-teal-700 text-teal-200'
                          : t.team_type === 'funding' ? 'bg-amber-950 border-amber-700 text-amber-200'
                          : t.team_type === 'applications' ? 'bg-violet-950 border-violet-700 text-violet-200'
                          :                         'bg-slate-800 border-slate-600 text-slate-200'
                          }`}
                          title={`${t.team_type_label}${t.is_lead ? ' — Team Lead' : ''}`}>
                      {t.is_lead ? '★ ' : ''}{t.name}
                    </span>
                  ))}
                </div>
              )}
              <div className={isInternalOperator ? '' : 'flex items-center gap-2'}>
                {isInternalOperator ? (
                  <OperatorInput
                    userId={user?.user_id || user?.id}
                    value={chatInput}
                    onChange={setChatInput}
                    onSubmit={(v) => handleChatSend(v)}
                    disabled={chatSending}
                    locked={!!cmdSelectedClient}
                    placeholder="Command Ghost (e.g. Mark John Doe Active)"
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={handleChatKeyDown}
                      placeholder="Ask Ghost anything..."
                      className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
                      disabled={chatSending}
                      data-testid="ghost-chat-input"
                    />
                    <button
                      onClick={() => handleChatSend()}
                      disabled={chatSending || !chatInput.trim()}
                      className="p-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40 transition-colors"
                      data-testid="ghost-chat-send-btn"
                    >
                      <Send className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
              <button onClick={handleOpenBuilder} className="w-full flex items-center justify-center gap-2 text-[10px] text-slate-400 hover:text-slate-600 py-1 transition-colors" data-testid="ghost-open-builder">
                <MessageSquare className="h-3 w-3" />
                Open Ghost Deal Builder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Builder view */}
      {open && view === 'builder' && (
        <div style={{ zIndex: 99999, position: 'relative' }}>
          <GhostDealBuilder
            open={true}
            onClose={handleBuilderClose}
            clientContext={clientContext}
            onDealCreated={handleDealCreated}
          />
        </div>
      )}
    </>,
    document.body
  );
};

export default GhostFloatingIcon;
